import { useState } from 'react'
import { Formik } from 'formik'
import omit from 'lodash/omit'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useCreatePromoCode,
  useGeneratePromoCodes,
  useUpdatePromoCode,
  type Carrier,
  type GeneratePromoCodesCommand,
  type PromoCodeDto as ApiPromoCodeDto,
} from '@dhl/mdp-api'
import { Box, Card } from '@dhl-ui/components'
import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { downloadAnything } from '~/common/util/download'
import { getErrorMessage } from '~/common/util/axiosUtil'

import { CancelDialog, PromoCodeForm } from '~/modules/promocodes/components'
import { usePromoCode } from '~/modules/promocodes/hooks'
import {
  convertToPromoCodeDto,
  createPromoCodeValidationSchema,
  initialPromoCode,
  type PromoCode,
  type PromoCodeDto,
} from '~/modules/promocodes/model'
import { BackButton } from '~/temp-ds-component/BackButton'

type Params = {
  id: string
}

// Needed to convert the carriers from the local model to the API `Carrier` union.
// Aligning the API DTO and local model will be done separately soon™.
const toCarrier = (carrier: string): Carrier => {
  switch (carrier) {
    case 'DHL-PARCEL':
    case 'DHL-EXPRESS':
    case 'DHL-EXPRESS-ECONOMY':
      return carrier
    default:
      throw new Error(`Unsupported carrier: ${carrier}`)
  }
}

export const PromoCodeFormPage = () => {
  const { id } = useParams<Params>()
  const addNew = !id

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [generatePromocodes, setGeneratePromocodes] = useState<boolean>(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { mutateAsync: createPromoCode } = useCreatePromoCode()
  const { mutateAsync: updatePromoCode } = useUpdatePromoCode()
  const { mutateAsync: generatePromoCodes } = useGeneratePromoCodes()

  const { promoCode, isLoading } = usePromoCode(id)

  const onBack = (dirty: boolean) => (dirty ? setCancelDialogOpen(true) : navigate(links.promoCodesLink()))

  const handleChangeSwitch = (checked: boolean) => {
    setGeneratePromocodes(checked)
  }

  const validateSchema = createPromoCodeValidationSchema(t, generatePromocodes)

  const onSubmit = async (promoCode: PromoCode) => {
    const promoCodeDto = convertToPromoCodeDto(promoCode, generatePromocodes)
    if (!generatePromocodes) {
      savePromoCode(promoCodeDto).then(() => {})
    } else {
      downloadPromoCodes(promoCodeDto).then(() => {})
    }
  }

  const savePromoCode = async (promoCodeDto: PromoCodeDto) => {
    try {
      const apiPromoCodeDto: ApiPromoCodeDto = {
        ...promoCodeDto,
        carriers: promoCodeDto.carriers.map(toCarrier),
        key: promoCodeDto.key ?? '',
      }
      if (addNew) {
        await createPromoCode({ data: apiPromoCodeDto })
      } else {
        await updatePromoCode({ key: id, data: apiPromoCodeDto })
      }
      const message = t(`admin.promoCodesPage.${addNew ? 'add' : 'update'}SuccessMessage`)
      showSuccess(message)
      navigate(id ? links.promoCodeDetailsLink(id) : links.promoCodesLink())
    } catch (error) {
      const message = getErrorMessage(error, t('admin.promoCodesPage.errorMessage'))
      showError(message)
    }
  }

  const downloadPromoCodes = async (promoCodeDto: PromoCodeDto) => {
    try {
      const command = omit(promoCodeDto, ['key', 'number'])
      const generatePromoCodesCommand: GeneratePromoCodesCommand = {
        ...command,
        carriers: promoCodeDto.carriers.map(toCarrier),
        number: promoCodeDto.number ?? 0,
      }
      const generatedPromoCodes = await generatePromoCodes({ data: generatePromoCodesCommand })
      downloadAnything('promocodes.txt', generatedPromoCodes)
    } catch (error) {
      const message = getErrorMessage(error, t('admin.promoCodesPage.errorMessage'))
      showError(message)
    }
  }

  if (isLoading) {
    return null
  }

  return (
    <>
      <Formik
        initialValues={promoCode ?? initialPromoCode}
        validateOnMount
        onSubmit={onSubmit}
        validationSchema={validateSchema}
      >
        {({ dirty }) => (
          <>
            <Box marginBottom={2}>
              <BackButton onClick={() => onBack(dirty)}>{t('admin.promoCodesPage.backButton')}</BackButton>
            </Box>
            <Box>
              <Card>
                <Card.Header title={t('admin.promoCodesPage.promoCodeForm.title')} />
                <Card.Content>
                  <PromoCodeForm handleChangeSwitch={handleChangeSwitch} id={id} />
                </Card.Content>
              </Card>
            </Box>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
